/**
 * The "exchange rate" number type: a column that fills itself with the rate of
 * a currency pair on the row's date, taken from the Numtable Exchange rates
 * document (`@/shared/exchangeRates/history`).
 *
 * It works like a formula column: a cell stays linked to the source, and is
 * rewritten whenever its rate moves (a "Today" rate every day, a new setup, a
 * corrected quote), until someone overrides it. The rate is still stored in the
 * row, so everything that reads rows sees it; what tells the rule's value from
 * an override is a marker with the last value the rule wrote
 * (`exchangeRateAutoField`, kept with the grid's cell overrides). A cell whose
 * value no longer matches its marker was written by someone else (typed, MCP,
 * an automation) and is left alone; clearing it links it again. With
 * `allowOverwrite` off the cell is read-only and always the rate.
 */
import { FX_MARKETS, FX_SIDES, fxRateOn, type FxHistory, type FxMarket, type FxSide } from './fxRate';
import { isCurrency, type Currency } from '../currencies';
import { parseGridDate, serialToDate } from '../formula/dateSerial';

/**
 * Units of `to` per 1 `from`, taken from the Numtable Exchange rates document
 * for the date in `dateField` (or the latest earlier day with a quote).
 * `market` only matters when one side is ARS; `side` picks buy, sell or the
 * average of the two. `fixed` marks a module's preset (Investments) whose
 * pair and date column the format bar shows but does not let anyone change.
 */
export interface GridExchangeRate {
  from: Currency;
  to: Currency;
  dateField: string;
  market?: FxMarket;
  /** `rule`: the quote follows another column of the row (`sideRule`). */
  side?: FxSide | 'rule';
  /** With `side: 'rule'`: values of `field` that take the sell quote, those that
   *  take the buy quote; any other value takes the average. */
  sideRule?: GridExchangeRateSideRule;
  /** Unset or true: a typed value stays. False: read-only, always the rate. */
  allowOverwrite?: boolean;
  fixed?: boolean;
}

export interface GridExchangeRateSideRule {
  field: string;
  sell: string[];
  buy: string[];
}

function normalizeSideRule(value: unknown): GridExchangeRateSideRule | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (typeof raw.field !== 'string' || raw.field === '') return undefined;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : []);
  return { field: raw.field, sell: list(raw.sell), buy: list(raw.buy) };
}

export function normalizeGridExchangeRate(value: unknown): GridExchangeRate | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (!isCurrency(raw.from) || !isCurrency(raw.to) || typeof raw.dateField !== 'string' || raw.dateField === '') return undefined;
  return {
    from: raw.from,
    to: raw.to,
    dateField: raw.dateField,
    ...((FX_MARKETS as readonly unknown[]).includes(raw.market) ? { market: raw.market as FxMarket } : {}),
    ...((FX_SIDES as readonly unknown[]).includes(raw.side) ? { side: raw.side as FxSide } : {}),
    ...(raw.side === 'rule' && normalizeSideRule(raw.sideRule)
      ? { side: 'rule' as const, sideRule: normalizeSideRule(raw.sideRule)! }
      : {}),
    ...(raw.allowOverwrite === false ? { allowOverwrite: false } : {}),
    ...(raw.fixed === true ? { fixed: true } : {}),
  };
}


/** The quote a row takes: the configured side, or what the custom rule says for its condition cell. */
export function exchangeRateSideFor(rule: GridExchangeRate, row: Readonly<Record<string, unknown>>): FxSide {
  if (rule.side !== 'rule') return rule.side ?? 'avg';
  const condition = rule.sideRule ? String(row[rule.sideRule.field] ?? '').trim().toLowerCase() : '';
  if (condition === '' || !rule.sideRule) return 'avg';
  const has = (list: readonly string[]) => list.some((value) => value.trim().toLowerCase() === condition);
  if (has(rule.sideRule.sell)) return 'sell';
  if (has(rule.sideRule.buy)) return 'buy';
  return 'avg';
}

const isEmpty = (value: unknown) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/** A date cell as YYYY-MM-DD: a Date, a sheet serial or date text. */
/** `dateField` value that rates every row on the current day instead of a column's date. */
export const EXCHANGE_RATE_TODAY = '@today';

export function isoDayOf(value: unknown): string | null {
  const date = value instanceof Date
    ? value
    : typeof value === 'number' && Number.isFinite(value)
      ? serialToDate(value)
      : typeof value === 'string'
        ? parseGridDate(value)
        : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Cell-override field holding the last rate the rule wrote into `field`. */
export const exchangeRateAutoField = (field: string) => `fx:${field}`;

const roundRate = (rate: number) => Math.round(rate * 1e6) / 1e6;
const sameRate = (a: number, b: number) => Math.abs(a - b) < 1e-9;

/** Whether `value` is a rate the history ever gave for the pair, under any market or quote. */
function wasSomeRate(history: FxHistory, rule: GridExchangeRate, value: number): boolean {
  for (const day of history.dates) {
    for (const market of FX_MARKETS) {
      for (const side of FX_SIDES) {
        const rate = fxRateOn(history, { from: rule.from, to: rule.to, market, side }, day);
        if (rate !== null && sameRate(roundRate(rate), value)) return true;
      }
    }
  }
  return false;
}

/**
 * Whether the cell still follows the source. `auto` is the last rate the rule
 * wrote there; a cell from before the markers (`auto` undefined) counts as
 * linked when it holds some rate of the pair, since nobody types those exactly.
 */
function isLinked(rule: GridExchangeRate, value: unknown, auto: number | undefined, history: FxHistory): boolean {
  if (rule.allowOverwrite === false || isEmpty(value)) return true;
  if (typeof value !== 'number') return false;
  return auto !== undefined ? sameRate(value, auto) : wasSomeRate(history, rule, value);
}

/**
 * The rates to write into `row`: every linked cell whose rate differs from the
 * one its date (or today) has now. With `changed`, only columns whose date,
 * quote condition or own value was just edited are looked at. Rates are
 * rounded to six decimals, which is more than any source quotes.
 */
export function exchangeRateChanges(
  rules: Readonly<Record<string, GridExchangeRate>>,
  row: Readonly<Record<string, unknown>>,
  changed: ReadonlySet<string> | 'all',
  history: FxHistory | null,
  autoOf: (field: string) => number | undefined = () => undefined,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!history) return out;
  for (const [field, rule] of Object.entries(rules)) {
    const conditionField = rule.side === 'rule' ? rule.sideRule?.field : undefined;
    if (changed !== 'all' && !changed.has(rule.dateField) && !changed.has(field) && !(conditionField && changed.has(conditionField))) continue;
    if (!isLinked(rule, row[field], autoOf(field), history)) continue;
    const day = rule.dateField === EXCHANGE_RATE_TODAY ? isoDayOf(new Date()) : isoDayOf(row[rule.dateField]);
    if (!day) continue;
    const rate = fxRateOn(history, { ...rule, side: exchangeRateSideFor(rule, row) }, day);
    if (rate === null) continue;
    const next = roundRate(rate);
    if (typeof row[field] !== 'number' || !sameRate(row[field], next)) out.set(field, next);
  }
  return out;
}
